import { z } from 'zod';

import { jsonRequest } from './shared';

const OfflinePaymentStatusSchema = z.enum(['queued', 'processing', 'synced', 'failed']);

const RawOfflinePaymentTokenSchema = z.object({
  id: z.string(),
  queueId: z.string(),
  status: OfflinePaymentStatusSchema,
  replayStatus: OfflinePaymentStatusSchema,
  lastError: z.string().nullable().optional(),
  attemptCount: z.number(),
  locationId: z.string().nullable().optional(),
  amountCents: z.number(),
  currency: z.string(),
  orderId: z.string().nullable().optional(),
  paymentId: z.string().nullable().optional(),
  updatedAt: z.union([z.string(), z.number()]),
  nextAttemptAt: z.union([z.string(), z.number()]).nullable().optional(),
  createdAt: z.union([z.string(), z.number()]).nullable().optional(),
});

const OfflinePaymentsResponseSchema = z.object({
  ok: z.literal(true),
  tokens: z.array(RawOfflinePaymentTokenSchema),
});

export type OfflinePaymentReplayStatus = z.infer<typeof OfflinePaymentStatusSchema>;

export interface OfflinePaymentTokenStatus {
  id: string;
  queueId: string;
  status: OfflinePaymentReplayStatus;
  replayStatus: OfflinePaymentReplayStatus;
  lastError: string | null;
  attemptCount: number;
  locationId: string | null;
  amountCents: number;
  currency: string;
  orderId: string | null;
  paymentId: string | null;
  updatedAt: number;
  nextAttemptAt: number | null;
  createdAt: number | null;
}

const toMillis = (value: string | number | Date | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const mapToken = (token: z.infer<typeof RawOfflinePaymentTokenSchema>): OfflinePaymentTokenStatus => ({
  id: token.id,
  queueId: token.queueId,
  status: token.status,
  replayStatus: token.replayStatus,
  lastError: token.lastError ?? null,
  attemptCount: token.attemptCount,
  locationId: token.locationId ?? null,
  amountCents: token.amountCents,
  currency: token.currency,
  orderId: token.orderId ?? null,
  paymentId: token.paymentId ?? null,
  updatedAt: toMillis(token.updatedAt) ?? Date.now(),
  nextAttemptAt: toMillis(token.nextAttemptAt ?? null),
  createdAt: toMillis(token.createdAt ?? null),
});

export interface FetchOfflinePaymentTokensParams {
  status?: OfflinePaymentReplayStatus;
  replayStatus?: OfflinePaymentReplayStatus;
  limit?: number;
}

export const OFFLINE_PAYMENT_TOKENS_QUERY_KEY = ['offline', 'payments', 'tokens'] as const;

export async function fetchOfflinePaymentTokens(
  params: FetchOfflinePaymentTokensParams = {},
): Promise<OfflinePaymentTokenStatus[]> {
  const search = new URLSearchParams();
  if (params.status) {
    search.set('status', params.status);
  }
  if (params.replayStatus) {
    search.set('replayStatus', params.replayStatus);
  }
  if (params.limit) {
    search.set('limit', String(params.limit));
  }

  const suffix = search.toString();
  const payload = await jsonRequest(`/offline/payments${suffix ? `?${suffix}` : ''}`, {
    method: 'GET',
    schema: OfflinePaymentsResponseSchema,
  });

  return payload.tokens.map(mapToken);
}

const ReplayResponseSchema = z.object({
  ok: z.literal(true),
  id: z.string(),
  replayStatus: OfflinePaymentStatusSchema,
});

export const replayOfflinePaymentToken = (id: string): Promise<z.infer<typeof ReplayResponseSchema>> =>
  jsonRequest(`/offline/payments/${encodeURIComponent(id)}/replay`, {
    method: 'POST',
    schema: ReplayResponseSchema,
  });

export const failOfflinePaymentToken = (
  id: string,
  reason?: string,
): Promise<z.infer<typeof ReplayResponseSchema>> =>
  jsonRequest(`/offline/payments/${encodeURIComponent(id)}/fail`, {
    method: 'POST',
    schema: ReplayResponseSchema,
    body: reason ? { reason } : undefined,
  });
